import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import tourStepsData from './tourSteps.json';
import './OnboardingTourModal.css';

// Helper function to extract YouTube video ID and create embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return '';
  
  // Extract video ID from various YouTube URL formats
  let videoId = '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      videoId = match[1];
      break;
    }
  }
  
  if (!videoId) return ''; // Return empty if not a YouTube URL
  
  // Return embed URL without autoplay
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`;
};

const OnboardingTourModal = ({ visible, onClose, onSkip }) => {
  const [currentView, setCurrentView] = useState('welcome'); // 'welcome' or 'tour'
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = tourStepsData.tourSteps.length;

  useEffect(() => {
    if (visible) {
      setCurrentView('welcome');
      setCurrentStep(0);
      console.log('Onboarding tour opened');
      console.log('Tour steps data:', tourStepsData);
    }
  }, [visible]);

  const handleStartTour = () => {
    setCurrentView('tour');
  };

  const handleGetStarted = () => {
    onSkip();
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDotClick = (index) => {
    setCurrentStep(index);
  };

  const handleFinish = () => {
    onClose();
  };

  const handleKeyPress = (e) => {
    if (currentView === 'tour') {
      if (e.key === 'ArrowRight' && currentStep < totalSteps - 1) {
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        handlePrev();
      } else if (e.key === 'Escape') {
        onSkip();
      }
    }
  };

  useEffect(() => {
    if (visible && currentView === 'tour') {
      window.addEventListener('keydown', handleKeyPress);
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [visible, currentView, currentStep]);

  const currentStepData = tourStepsData.tourSteps[currentStep];

  return (
    <Modal
      visible={visible}
      onCancel={onSkip}
      footer={null}
      width={currentView === 'welcome' ? 900 : 800}
      centered
      destroyOnClose={true}
      className="onboarding-tour-modal"
      closable={false}
    >
      {currentView === 'welcome' ? (
        <div className="onboarding-welcome-screen">
          <div className="welcome-close-btn" onClick={onSkip}>
            <CloseOutlined style={{ fontSize: '16px', color: '#8C8C8C' }} />
          </div>
          
          <h2 className="welcome-title manrope">Welcome to AI Image Generator! 🎨</h2>
          <p className="welcome-subtitle manrope">
            Watch our tutorial video to learn how to create stunning AI-generated images in just a few steps
          </p>

          <div className={`welcome-video-container ${!getYouTubeEmbedUrl(tourStepsData.welcomeVideo.url) ? 'has-thumbnail' : ''}`}>
            {getYouTubeEmbedUrl(tourStepsData.welcomeVideo.url) ? (
              <iframe
                src={getYouTubeEmbedUrl(tourStepsData.welcomeVideo.url)}
                title="Tutorial Video"
                className="welcome-video-embed"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <img 
                  src={tourStepsData.welcomeVideo.thumbnail} 
                  alt="Tutorial Video"
                  className="welcome-video-thumbnail"
                  onError={(e) => {
                    console.error('Failed to load video thumbnail:', tourStepsData.welcomeVideo.thumbnail);
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360"%3E%3Crect fill="%23f5f5f5" width="640" height="360"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="30" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EVideo Preview%3C/text%3E%3C/svg%3E';
                  }}
                />
                <div 
                  className="video-play-overlay"
                  onClick={() => {
                    if (tourStepsData.welcomeVideo.url) {
                      window.open(tourStepsData.welcomeVideo.url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <div className="play-icon">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                      <circle cx="32" cy="32" r="32" fill="white" fillOpacity="0.95"/>
                      <path d="M26 20L44 32L26 44V20Z" fill="#1890ff"/>
                    </svg>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="welcome-actions">
            <Button 
              className="welcome-btn-secondary manrope"
              size="large"
              onClick={handleGetStarted}
            >
              Get Started
            </Button>
            <Button 
              className="welcome-btn-primary manrope"
              type="primary"
              size="large"
              onClick={handleStartTour}
            >
              Start Tour
            </Button>
          </div>

          <p className="welcome-note manrope">
            You can restart this tour anytime from the help icon
          </p>
        </div>
      ) : (
        <div className="onboarding-tour-carousel">
          <div className="tour-close-btn" onClick={onSkip}>
            <CloseOutlined style={{ fontSize: '16px', color: '#8C8C8C' }} />
          </div>

          <div className="tour-content">
            <div className="tour-image-container">
              <img 
                src={currentStepData.image} 
                alt={currentStepData.title}
                className="tour-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', currentStepData.image);
                  console.error('Current step:', currentStep, 'Step data:', currentStepData);
                  e.target.onerror = null;
                  e.target.src = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="700" height="400" viewBox="0 0 700 400"%3E%3Crect fill="%23f0f2f5" width="700" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="24" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EStep ${currentStepData.id}%3C/text%3E%3C/svg%3E`;
                }}
              />
              {currentStepData.highlights && currentStepData.highlights.map((highlight, index) => (
                <div
                  key={index}
                  className={`tour-highlight tour-highlight-${highlight.type}`}
                  style={{
                    left: highlight.x,
                    top: highlight.y,
                    width: highlight.size,
                    height: highlight.size,
                    '--highlight-color': highlight.color || '#3b82f6',
                  }}
                />
              ))}
            </div>

            <div className="tour-text-content">
              <div className="tour-step-indicator manrope">
                Step {currentStep + 1} of {totalSteps}
              </div>
              <h3 className="tour-step-title manrope">{currentStepData.title}</h3>
              <p className="tour-step-description manrope">{currentStepData.description}</p>
            </div>
          </div>

          <div className="tour-navigation">
            <div className="tour-progress-dots">
              {tourStepsData.tourSteps.map((step, index) => (
                <div
                  key={step.id}
                  className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </div>

            <div className="tour-nav-buttons">
              <Button
                className="tour-nav-btn manrope"
                onClick={handlePrev}
                disabled={currentStep === 0}
                icon={<LeftOutlined />}
              >
                Previous
              </Button>

              {currentStep === totalSteps - 1 ? (
                <Button
                  className="tour-nav-btn-primary manrope"
                  type="primary"
                  onClick={handleFinish}
                >
                  Finish Tour
                </Button>
              ) : (
                <Button
                  className="tour-nav-btn-primary manrope"
                  type="primary"
                  onClick={handleNext}
                >
                  Next
                  <RightOutlined />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default OnboardingTourModal;
